// Save sanitization (IMPLEMENTATION §3 M11 "injected NaN is sanitized and logged").
// A number that is not finite never reaches the running sim or the storage:
//   load   NaN / ±Infinity anywhere in the raw envelope (an object handed to __gm.load, `1e999` in a text
//          save) → 0, or the key is dropped inside the `meta.settings` / `meta.uiFlags` records (the
//          default applies). After migrating, a JSON `null` where the CURRENT format has a number (what
//          JSON.stringify writes for NaN) → 0, guided by a template envelope (plain objects and number
//          arrays only: containers of entities differ per entry and are left to the sim's invariants).
//          The sim then validates the repaired state (integers only + invariants): a repair that leaves it
//          inconsistent (e.g. a NaN id → 0 → duplicate) is still rejected as corrupt (→ .bak).
//   write  the same replacement inside JSON.stringify, so a stray NaN never becomes an unloadable `null`.
// Every repair is reported (the SaveService logs one `save_sanitized` telemetry row per load / write).

export interface SanitizeFix {
  /** Dotted path, e.g. `sim.wallet.cash`, `sim.floor[2].x`, `meta.settings.volume`. */
  path: string;
  /** What was found: 'NaN', 'Infinity', '-Infinity' or 'null'. */
  was: string;
  /** What it became: '0' or 'dropped'. */
  now: '0' | 'dropped';
}

/** Records whose non-finite entries are dropped (so the default applies) instead of zeroed. */
const DROP_UNDER = ['meta.settings', 'meta.uiFlags'] as const;

/** Marks a non-finite number inside a JSON copy (a save never contains a NUL character). */
const NF = '\u0000gm-nonfinite:';

type Json = null | boolean | number | string | Json[] | { [k: string]: Json };

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * A deep JSON copy of `raw` that KEEPS non-finite numbers visible (JSON.stringify would turn them into
 * null). Throws on what JSON cannot hold (cycles, BigInt).
 */
export function jsonCopyKeepingNonFinite(raw: unknown): unknown {
  const text = JSON.stringify(raw, (_k, v: unknown) =>
    typeof v === 'number' && !Number.isFinite(v) ? `${NF}${String(v)}` : v,
  );
  if (text === undefined) throw new TypeError('not JSON');
  return JSON.parse(text) as Json;
}

function nonFiniteOf(v: unknown): string | null {
  if (typeof v === 'number') return Number.isFinite(v) ? null : String(v);
  if (typeof v === 'string' && v.startsWith(NF)) return v.slice(NF.length);
  return null;
}

/** Replaces non-finite numbers (and the markers of `jsonCopyKeepingNonFinite`) in place. */
export function sanitizeNonFinite(root: unknown): SanitizeFix[] {
  const fixes: SanitizeFix[] = [];
  const walk = (v: unknown, path: string): void => {
    if (Array.isArray(v)) {
      for (let i = 0; i < v.length; i++) {
        const p = `${path}[${i}]`;
        const was = nonFiniteOf(v[i]);
        if (was !== null) {
          v[i] = 0;
          fixes.push({ path: p, was, now: '0' });
        } else walk(v[i], p);
      }
      return;
    }
    if (!isRecord(v)) return;
    const drop = (DROP_UNDER as readonly string[]).includes(path);
    for (const k of Object.keys(v)) {
      const p = path === '' ? k : `${path}.${k}`;
      const was = nonFiniteOf(v[k]);
      if (was !== null) {
        if (drop) delete v[k];
        else v[k] = 0;
        fixes.push({ path: p, was, now: drop ? 'dropped' : '0' });
      } else walk(v[k], p);
    }
  };
  walk(root, '');
  return fixes;
}

/**
 * After migrating: a `null` where `template` (a current-format envelope) holds a number becomes 0. Walks
 * plain objects present in both and arrays of numbers; never adds or removes keys.
 */
export function repairNulls(obj: unknown, template: unknown, path = ''): SanitizeFix[] {
  const fixes: SanitizeFix[] = [];
  if (Array.isArray(obj) && Array.isArray(template)) {
    if (template.length === 0 || !template.every((x) => typeof x === 'number')) return fixes;
    for (let i = 0; i < obj.length; i++) {
      if (obj[i] === null) {
        obj[i] = 0;
        fixes.push({ path: `${path}[${i}]`, was: 'null', now: '0' });
      }
    }
    return fixes;
  }
  if (!isRecord(obj) || !isRecord(template)) return fixes;
  for (const k of Object.keys(template)) {
    if (!Object.hasOwn(obj, k)) continue;
    const p = path === '' ? k : `${path}.${k}`;
    const t = template[k];
    const o = obj[k];
    if (typeof t === 'number' && o === null) {
      obj[k] = 0;
      fixes.push({ path: p, was: 'null', now: '0' });
    } else fixes.push(...repairNulls(o, t, p));
  }
  return fixes;
}

/**
 * JSON.stringify for a save: non-finite numbers become 0 (or are dropped inside `dropIn` records) and are
 * reported in `fixes` (property names; the replacer has no full path).
 */
export function stringifySanitized(
  value: unknown,
  dropIn: readonly object[],
  fixes: SanitizeFix[],
): string {
  return JSON.stringify(value, function (this: unknown, k: string, v: unknown) {
    if (typeof v === 'number' && !Number.isFinite(v)) {
      const drop = typeof this === 'object' && this !== null && dropIn.includes(this);
      fixes.push({ path: k, was: String(v), now: drop ? 'dropped' : '0' });
      return drop ? undefined : 0;
    }
    return v;
  });
}

/** One telemetry `extra` text: the first `max` fixes (`path=was→now`), then `…+N`. */
export function describeFixes(fixes: readonly SanitizeFix[], max: number): string {
  const shown = fixes.slice(0, Math.max(0, max)).map((f) => `${f.path}=${f.was}→${f.now}`);
  const more = fixes.length - shown.length;
  return more > 0 ? `${shown.join(';')};…+${more}` : shown.join(';');
}
